/**
 * useWatchedSymbols — persists the user's Live Coach Feed ticker selection.
 *
 * The Live Coach Feed (Setups firing now + Counter-Bias Whisper) needs to
 * know which symbol(s) to poll bars for. Two modes:
 *
 *   - AUTO (empty array) — follow the most recent trade's symbol. This was
 *     the original v1 behavior; works fine for single-symbol traders but
 *     invisible (no UI cue, no way to override).
 *   - EXPLICIT (1-2 strings) — watch these specific tickers regardless of
 *     what the user is trading. Use cases: scalping MNQ but watching MES
 *     for confluence; monitoring two related instruments side-by-side.
 *
 * Hard cap of 2 is intentional. Three+ tickers would crowd the Coach panel
 * past readability, and the "MNQ + MES as confluence" pattern is by far the
 * most common multi-ticker workflow we've seen. If demand for more emerges,
 * the limit can be lifted — but the layout would need to switch to a
 * vertical-scrolling list rather than the current side-by-side grid.
 *
 * Why localStorage instead of settings.json:
 *   - Per-user/per-machine preference, no cross-device sync needed
 *   - Doesn't bloat settings.json (already 24+ MB)
 *   - Faster iteration; can promote to settings later if cloud sync added
 */

import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'journifi.liveCoach.watchedSymbols';
export const MAX_WATCHED = 2;

// Multi-TF alignment pill timeframes — separate localStorage key, same
// pattern as watchedSymbols. Kept here so both Live Coach prefs live in
// one place; promote to its own hook file if either grows much past this.
const TF_STORAGE_KEY = 'journifi.liveCoach.alignmentTimeframes';
export const MAX_ALIGNMENT_TFS = 3;
// Default trio matches the multi-TF stack most users describe ("higher
// timeframe context → trade timeframe"). 15m is the upper edge of the
// scalping zone; 1h confirms swing structure; 4h frames the day.
export const DEFAULT_ALIGNMENT_TFS = ['15m', '1h', '4h'];
// The full set of timeframes a user can pick from (matches what
// barDataService can fetch — anything aggregated from 1m).
export const ALLOWED_ALIGNMENT_TFS = ['1m', '5m', '15m', '30m', '1h', '2h', '4h', '1d'];

const readStorage = () => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(s => typeof s === 'string' && s.trim().length > 0)
      .map(s => s.trim().toUpperCase())
      .slice(0, MAX_WATCHED);
  } catch {
    return [];
  }
};

const writeStorage = (symbols) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(symbols));
  } catch {
    // localStorage full or unavailable — swallow
  }
};

/**
 * @returns {Object}
 *   {
 *     watchedSymbols,  // string[] — current explicit selection (empty = auto)
 *     addSymbol,       // (sym) => void — push a symbol, no-op if already at MAX
 *     removeSymbol,    // (sym) => void — drop by exact match
 *     setSymbols,      // (syms[]) => void — replace wholesale
 *     clear,           // () => void — back to auto mode
 *     isAuto,          // boolean — true when no explicit symbols set
 *     atMax,           // boolean — true when 2 symbols selected
 *   }
 */
export const useWatchedSymbols = () => {
  // Lazy init from storage — avoids a flash of "auto" before hydration.
  const [watchedSymbols, setWatchedSymbolsState] = useState(readStorage);

  // Persist on every change.
  useEffect(() => {
    writeStorage(watchedSymbols);
  }, [watchedSymbols]);

  const setSymbols = useCallback((next) => {
    if (!Array.isArray(next)) return;
    const cleaned = next
      .filter(s => typeof s === 'string' && s.trim().length > 0)
      .map(s => s.trim().toUpperCase())
      .slice(0, MAX_WATCHED);
    // Dedupe while preserving order
    const seen = new Set();
    const deduped = [];
    for (const s of cleaned) {
      if (seen.has(s)) continue;
      seen.add(s);
      deduped.push(s);
    }
    setWatchedSymbolsState(deduped);
  }, []);

  const addSymbol = useCallback((sym) => {
    if (typeof sym !== 'string' || !sym.trim()) return;
    const s = sym.trim().toUpperCase();
    setWatchedSymbolsState(prev => {
      if (prev.includes(s)) return prev;
      if (prev.length >= MAX_WATCHED) return prev;
      return [...prev, s];
    });
  }, []);

  const removeSymbol = useCallback((sym) => {
    if (typeof sym !== 'string') return;
    const s = sym.trim().toUpperCase();
    setWatchedSymbolsState(prev => prev.filter(x => x !== s));
  }, []);

  const clear = useCallback(() => setWatchedSymbolsState([]), []);

  return {
    watchedSymbols,
    addSymbol,
    removeSymbol,
    setSymbols,
    clear,
    isAuto: watchedSymbols.length === 0,
    atMax: watchedSymbols.length >= MAX_WATCHED,
  };
};

// ── Alignment timeframes hook — sibling to useWatchedSymbols ──────────

const readTfStorage = () => {
  if (typeof window === 'undefined') return DEFAULT_ALIGNMENT_TFS;
  try {
    const raw = window.localStorage.getItem(TF_STORAGE_KEY);
    if (!raw) return DEFAULT_ALIGNMENT_TFS;
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return DEFAULT_ALIGNMENT_TFS;
    const cleaned = parsed
      .filter(s => typeof s === 'string' && ALLOWED_ALIGNMENT_TFS.includes(s.toLowerCase()))
      .map(s => s.toLowerCase())
      .slice(0, MAX_ALIGNMENT_TFS);
    return cleaned.length > 0 ? cleaned : DEFAULT_ALIGNMENT_TFS;
  } catch {
    return DEFAULT_ALIGNMENT_TFS;
  }
};

const writeTfStorage = (timeframes) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(TF_STORAGE_KEY, JSON.stringify(timeframes));
  } catch {
    // swallow
  }
};

/**
 * Persists the user's choice of timeframes for the Live Coach alignment pills.
 *
 * Defaults to ['15m', '1h', '4h']. Always returns exactly MAX_ALIGNMENT_TFS
 * entries (currently 3); setting a different-length value clamps/pads to
 * keep the UI's 3-pill layout stable.
 *
 * @returns {Object}
 *   {
 *     timeframes,        // string[] — current trio (length === MAX_ALIGNMENT_TFS)
 *     setTimeframeAt,    // (index, tf) => void — swap one slot
 *     resetTimeframes,   // () => void — back to defaults
 *   }
 */
export const useAlignmentTimeframes = () => {
  const [timeframes, setTimeframesState] = useState(readTfStorage);

  useEffect(() => {
    writeTfStorage(timeframes);
  }, [timeframes]);

  const setTimeframeAt = useCallback((index, tf) => {
    if (typeof tf !== 'string') return;
    const lower = tf.toLowerCase();
    if (!ALLOWED_ALIGNMENT_TFS.includes(lower)) return;
    if (!Number.isInteger(index) || index < 0 || index >= MAX_ALIGNMENT_TFS) return;
    setTimeframesState(prev => {
      const next = prev.slice();
      // Pad to MAX_ALIGNMENT_TFS so index access is safe; reuse defaults
      // for any missing slots.
      while (next.length < MAX_ALIGNMENT_TFS) {
        next.push(DEFAULT_ALIGNMENT_TFS[next.length] || '5m');
      }
      next[index] = lower;
      return next.slice(0, MAX_ALIGNMENT_TFS);
    });
  }, []);

  const resetTimeframes = useCallback(() => {
    setTimeframesState([...DEFAULT_ALIGNMENT_TFS]);
  }, []);

  return { timeframes, setTimeframeAt, resetTimeframes };
};

export default useWatchedSymbols;
